import { Autocomplete as MuiAutocomplete } from '@mui/material';
import { CSSProperties, useState } from 'react';
import { Text } from '../common/Text';
import { TextInput } from '../common/TextInput';
import { CheckSuccessIcon, EnterGray400Icon } from '@assets/icons';

interface ISearchInputProps {
  label?: string;
  boxstyle?: CSSProperties;
  placeholder?: string;
  disabled?: boolean;
  value: string | null;
  options: string[];
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onInputChange: (event: React.SyntheticEvent, newValue: string) => void;
  onAutocompleteChange?: (event: React.SyntheticEvent, value: string | null) => void;
  onComicNameUpdate?: (value: string) => void;
  isError?: boolean;
  showSubIcon?: boolean; // endAdornment에 서브아이콘 보여줄지 여부
}

// 피그마 'Text input' 컴포넌트 + 오토컴플리트 기능 제공
export const SearchInput = (props: ISearchInputProps) => {
  const {
    label,
    boxstyle,
    placeholder,
    disabled = false,
    value,
    options,
    onKeyDown,
    onInputChange,
    onAutocompleteChange,
    onComicNameUpdate,
    isError = false,
    showSubIcon = false
  } = props;

  const [openOptions, setOpenOptions] = useState<boolean>(false);
  const [showSuccessIcon, setShowSuccessIcon] = useState<boolean>(false);
  const [showEnterIcon, setShowEnterIcon] = useState<boolean>(false);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (onKeyDown) {
      onKeyDown(e);
    }

    const inputValue = (e.target as HTMLInputElement).value;
    if (e.key === 'Enter' && inputValue?.trim().length) {
      setShowSuccessIcon(true);
      setOpenOptions(false);
    }
  };

  const handleFocus = () => {
    setOpenOptions(true);
    setShowEnterIcon(true);
  };

  const handleBlur = () => {
    setOpenOptions(false);
    setShowEnterIcon(false);

    if (value?.trim().length) {
      setShowSuccessIcon(true);
    }
    if (onComicNameUpdate) {
      onComicNameUpdate(value ?? '');
    }
  };

  const handleInputChange = (event: React.SyntheticEvent, newValue: string) => {
    onInputChange(event, newValue);
    setShowSuccessIcon(false);

    if (newValue.length === 0) {
      if (openOptions) {
        setOpenOptions(false);
      }
    } else {
      if (!openOptions) {
        setOpenOptions(true);
      }
    }
  };

  return (
    <div style={boxstyle}>
      {label ? (
        <Text variant="subtitle" fontWeight="bold" marginBottom="8px">
          {label}
        </Text>
      ) : null}

      <MuiAutocomplete
        className="search-input-autocomplete" // theme.ts MuiAutocomplete style 적용을 위한 className 부여
        disabled={disabled}
        open={openOptions}
        inputValue={value ?? ''}
        options={options}
        onInputChange={handleInputChange}
        value={null} //inputValue와 options 요소가 일치할 시 onChange가 실행되지 않는 이슈 픽스
        onChange={(event: React.SyntheticEvent<Element, Event>, value: string | null) => {
          if (onAutocompleteChange) {
            onAutocompleteChange(event, value);
          }
          setShowSuccessIcon(true);
          setOpenOptions(false);
        }}
        isOptionEqualToValue={(option, value) => option === value} //inputValue와 options 요소가 일치할 시 onChange가 실행되지 않는 이슈 픽스
        renderInput={(params) => {
          return (
            <TextInput
              {...params}
              InputProps={{
                ...params.InputProps,
                style: { cursor: 'text' },
                endAdornment: showSubIcon ? (
                  <div style={{ paddingLeft: 16 }}>
                    {showSuccessIcon ? <CheckSuccessIcon /> : <>{showEnterIcon ? <EnterGray400Icon /> : null}</>}
                  </div>
                ) : null
              }}
              error={isError}
              placeholder={placeholder ?? ''}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          );
        }}
        clearIcon={false} // 값 입력시 보여지는 X 버튼을 보이지 않게 처리
      />
    </div>
  );
};
